import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { CompaniesFilterProvider } from "@/contexts/CompaniesFilterContext";
import { CompaniesContent } from "@/components/companies/CompaniesContent";
import { CompaniesFilterWrapper } from "@/components/companies/CompaniesFilterWrapper";
import { CompaniesActions } from "@/components/companies/CompaniesActions";
import { supabase } from "@/integrations/supabase/client";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";

const Companies = () => {
	usePageTitle("Companies");
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [viewMode, setViewMode] = usePersistedViewMode("companies");
	const { t } = useTranslation();

	const handleAddCompany = async (newCompanyData: {
		name: string;
		assignee: string;
	}) => {
		try {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) throw new Error("User not authenticated");

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (!userOrg) throw new Error("User organization not found");

			const { data, error } = await supabase
				.from('companies')
				.insert({
					name: newCompanyData.name,
					assignee: newCompanyData.assignee,
					organization_uuid: userOrg.organization_uuid
				})
				.select()
				.single();

			if (error) throw error;

			toast({
				title: t("common.success"),
				description: t("companies.addSuccess"),
			});

			setRefreshTrigger(prev => prev + 1);
			setIsAddModalOpen(false);
			return data;
		} catch (error) {
			console.error("Error adding company:", error);
			toast({
				title: t("common.error"),
				description: error instanceof Error ? error.message : "Failed to add company",
				variant: "destructive",
			});
			return null;
		}
	};

	return (
		<CompaniesFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
			<div>
				<PageHeader
					title={t("companies.header")}
					actions={
						<CompaniesActions
							isGenerating={isGenerating}
							setIsGenerating={setIsGenerating}
							fetchCompanies={() => setRefreshTrigger(prev => prev + 1)}
							onAddCompany={() => setIsAddModalOpen(true)}
						/>
					}
				/>

				<CompaniesFilterWrapper />

				<CompaniesContent
					onCompaniesChange={() => setRefreshTrigger(prev => prev + 1)}
				/>

				<AddCompanyForm
					isOpen={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onSubmit={handleAddCompany}
				/>
			</div>
		</CompaniesFilterProvider>
	);
};

export default Companies;
